// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider, FastField } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select, Divider } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartTextInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentTextField,
  ModalContentSwitch,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------
const MAX_SYMBOL_MAPPINGS = 50;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const SymbolAndTimeframeDefaults = {
  strSymbol: "Current",
  nTimeframe: "-2",
};

export const SymbolAndTimeframeSymbolMappingsDefaults = {
  useLive: true,
  useStrategy: false,
  stringToReplace: "Text",
  replacementString: "",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  strSymbol: {
    maxLength: 50,
    emptyAllowed: false,
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
  },
  symbolMapping: {
    maxLength: 50,
    emptyAllowed: false,
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=;]/,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const TIMEFRAME_OPTIONS = [
  { value: "-2", label: "Current" },
  { value: "0", label: "M1" },
  { value: "1", label: "M5" },
  { value: "2", label: "M15" },
  { value: "3", label: "M30" },
  { value: "4", label: "H1" },
  { value: "5", label: "H4" },
  { value: "6", label: "D1" },
  { value: "7", label: "W1" },
  { value: "8", label: "MN" },
];

// ----------------------------------------------------------------------
// Section - Helper Functions
// ----------------------------------------------------------------------
export function SymbolAndTimeframeRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const getDisplayValue = (key) => (config[key] !== undefined ? config[key] : "");

  const getTimeframeDisplay = () => {
    const value = config.nTimeframe;
    const timeframe = TIMEFRAME_OPTIONS.find((tf) => tf.value === value);
    return timeframe ? timeframe.label : "Not set";
  };

  // Parse symbol mappings
  let mappings = [];

  // Check for new array format first
  if (config.symbolMappings && Array.isArray(config.symbolMappings)) {
    mappings = config.symbolMappings.map((mappingData) => ({
      useLive: mappingData.useLive !== undefined ? mappingData.useLive : SymbolAndTimeframeSymbolMappingsDefaults.useLive,
      useStrategy: mappingData.useStrategy !== undefined ? mappingData.useStrategy : SymbolAndTimeframeSymbolMappingsDefaults.useStrategy,
      stringToReplace: mappingData.stringToReplace || SymbolAndTimeframeSymbolMappingsDefaults.stringToReplace,
      replacementString: mappingData.replacementString || SymbolAndTimeframeSymbolMappingsDefaults.replacementString,
    }));
  }

  return (
    <Stack spacing={0.5}>
      {renderParam("Symbol", getDisplayValue("strSymbol"))}
      {renderParam("Timeframe", getTimeframeDisplay())}
      <Divider sx={{ my: 0.5 }} />
      <Typography variant="contentTiny" sx={{ fontWeight: 600 }}>
        Symbol name mappings ({mappings.length}/{MAX_SYMBOL_MAPPINGS})
      </Typography>
      {mappings.map((mapping, index) => (
        <Typography key={index} variant="contentTiny">
          {(mapping.useLive || mapping.useStrategy) && (
            <span style={{ marginRight: 4, color: "#666" }}>
              {mapping.useLive && "Live"}
              {mapping.useLive && mapping.useStrategy && " & "}
              {mapping.useStrategy && "Tester"}:
            </span>
          )}
          {mapping.stringToReplace} → {mapping.replacementString}
        </Typography>
      ))}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------
export const SymbolAndTimeframeConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  SymbolAndTimeframeConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  useImperativeHandle(ref, () => ({
    openDialog: () => setOpen(true),
    closeDialog: () => setOpen(false),
  }));

  // Parse initial symbol mappings from initialData
  const parseInitialMappings = () => {
    const mappings = [];
    if (initialData) {
      // Check for new array format first
      if (initialData.symbolMappings && Array.isArray(initialData.symbolMappings)) {
        initialData.symbolMappings.forEach((mappingData, index) => {
          mappings.push({
            id: index + 1,
            useLive: mappingData.useLive !== undefined ? mappingData.useLive : SymbolAndTimeframeSymbolMappingsDefaults.useLive,
            useStrategy: mappingData.useStrategy !== undefined ? mappingData.useStrategy : SymbolAndTimeframeSymbolMappingsDefaults.useStrategy,
            stringToReplace: mappingData.stringToReplace || SymbolAndTimeframeSymbolMappingsDefaults.stringToReplace,
            replacementString: mappingData.replacementString || SymbolAndTimeframeSymbolMappingsDefaults.replacementString,
          });
        });
      }
    }
    return mappings.length > 0 ? mappings : [];
  };

  const [symbolMappings, setSymbolMappings] = useState(parseInitialMappings);

  const addNewMapping = () => {
    if (symbolMappings.length < MAX_SYMBOL_MAPPINGS) {
      const newId = Math.max(...symbolMappings.map((m) => m.id), 0) + 1;
      setSymbolMappings([
        ...symbolMappings,
        {
          id: newId,
          useLive: SymbolAndTimeframeSymbolMappingsDefaults.useLive,
          useStrategy: SymbolAndTimeframeSymbolMappingsDefaults.useStrategy,
          stringToReplace: SymbolAndTimeframeSymbolMappingsDefaults.stringToReplace,
          replacementString: SymbolAndTimeframeSymbolMappingsDefaults.replacementString,
        },
      ]);
    }
  };

  const removeMapping = (id) => {
    setSymbolMappings(symbolMappings.filter((mapping) => mapping.id !== id));
  };

  const updateMapping = (id, field, value) => {
    // For text fields, keep all text (no filtering)
    if (field === "stringToReplace" || field === "replacementString") {
      // Update with original value (no filtering)
      setSymbolMappings(symbolMappings.map((mapping) => (mapping.id === id ? { ...mapping, [field]: value } : mapping)));
    } else if (field === "useLive" || field === "useStrategy") {
      // For switches, ensure at least one remains selected
      const currentMapping = symbolMappings.find((m) => m.id === id);

      // If trying to disable a switch, check if the other one is enabled
      if (!value) {
        const otherField = field === "useLive" ? "useStrategy" : "useLive";
        if (!currentMapping[otherField]) {
          // Don't allow disabling if the other switch is already off
          return;
        }
      }

      // Update the switch value
      setSymbolMappings(symbolMappings.map((mapping) => (mapping.id === id ? { ...mapping, [field]: value } : mapping)));
    } else {
      // For other fields, update directly
      setSymbolMappings(symbolMappings.map((mapping) => (mapping.id === id ? { ...mapping, [field]: value } : mapping)));
    }
  };

  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(SymbolAndTimeframeDefaults).forEach((key) => {
      setFieldValue(key, SymbolAndTimeframeDefaults[key]);
    });
    // Reset symbol mappings values while preserving the array structure
    setSymbolMappings(symbolMappings.map(mapping => ({
      ...mapping,
      useLive: SymbolAndTimeframeSymbolMappingsDefaults.useLive,
      useStrategy: SymbolAndTimeframeSymbolMappingsDefaults.useStrategy,
      stringToReplace: SymbolAndTimeframeSymbolMappingsDefaults.stringToReplace,
      replacementString: SymbolAndTimeframeSymbolMappingsDefaults.replacementString,
    })));
  };

  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  const formik = useFormik({
    initialValues: {
      strSymbol: initialData?.strSymbol ?? SymbolAndTimeframeDefaults.strSymbol,
      nTimeframe: initialData?.nTimeframe ?? SymbolAndTimeframeDefaults.nTimeframe,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          // Check if any mapping has empty "String to replace"
          const hasEmptyStringToReplace = symbolMappings.some((mapping) => mapping.stringToReplace.trim() === "");

          if (hasEmptyStringToReplace) {
            setError(true);
            setReturnMessage("'String to replace' cannot be empty in any mapping.");
            setSubmitting(false);

            // Clear error message after 5 seconds
            setTimeout(() => {
              setError(false);
              setReturnMessage("");
            }, 5000);

            return;
          }

          // Check for validation errors in mappings
          const validationErrors = getMappingValidationErrors();
          if (Object.keys(validationErrors).length > 0) {
            const errorMessages = [];
            Object.values(validationErrors).forEach(errors => {
              Object.values(errors).forEach(error => {
                if (!errorMessages.includes(error)) {
                  errorMessages.push(error);
                }
              });
            });
            
            setError(true);
            setReturnMessage(`Validation errors: ${errorMessages.join(', ')}`);
            setSubmitting(false);

            // Clear error message after 5 seconds
            setTimeout(() => {
              setError(false);
              setReturnMessage("");
            }, 5000);

            return;
          }

          // Format symbol mappings as array - only include mappings with non-empty stringToReplace
          const formattedValues = { ...values };
          const validMappings = symbolMappings.filter((mapping) => mapping.stringToReplace.trim() !== "");

          // Create symbol mappings array
          formattedValues.symbolMappings = validMappings.map((mapping) => ({
            useLive: mapping.useLive,
            useStrategy: mapping.useStrategy,
            stringToReplace: mapping.stringToReplace,
            replacementString: mapping.replacementString,
          }));

          onSave(formattedValues);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } = formik;

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Validation Functions
  // ----------------------------------------------------------------------
  const validateMapping = (mapping) => {
    const errors = {};
    const limits = DEFAULT_LIMITS.symbolMapping;

    // Check for empty stringToReplace (required field)
    if (!limits.emptyAllowed && (!mapping.stringToReplace || mapping.stringToReplace.trim() === '')) {
      errors.stringToReplace = 'Field cannot be empty.';
    }
    // Check for invalid characters in stringToReplace
    else if (mapping.stringToReplace && limits.disallowedPattern && limits.disallowedPattern.test(mapping.stringToReplace)) {
      const invalidChars = mapping.stringToReplace.match(new RegExp(limits.disallowedPattern, "g"));
      const uniqueInvalidChars = [...new Set(invalidChars)];
      errors.stringToReplace = `Invalid characters detected: ${uniqueInvalidChars.join(", ")}`;
    }
    // Check for length limit in stringToReplace
    else if (mapping.stringToReplace && limits.maxLength && mapping.stringToReplace.length > limits.maxLength) {
      errors.stringToReplace = `Text too long. Maximum ${limits.maxLength} characters allowed.`;
    }
    
    // Check for invalid characters in replacementString (empty is allowed for replacement string)
    if (mapping.replacementString && limits.disallowedPattern && limits.disallowedPattern.test(mapping.replacementString)) {
      const invalidChars = mapping.replacementString.match(new RegExp(limits.disallowedPattern, "g"));
      const uniqueInvalidChars = [...new Set(invalidChars)];
      errors.replacementString = `Invalid characters detected: ${uniqueInvalidChars.join(", ")}`;
    }
    // Check for length limit in replacementString
    else if (mapping.replacementString && limits.maxLength && mapping.replacementString.length > limits.maxLength) {
      errors.replacementString = `Text too long. Maximum ${limits.maxLength} characters allowed.`;
    }
    
    return errors;
  };

  const getMappingValidationErrors = () => {
    const allErrors = {};
    symbolMappings.forEach(mapping => {
      const errors = validateMapping(mapping);
      if (Object.keys(errors).length > 0) {
        allErrors[mapping.id] = errors;
      }
    });
    return allErrors;
  };

  // Create emulated formik structure for SmartParameter error display
  const createMappingFormikStructure = () => {
    const mappingErrors = getMappingValidationErrors();
    const emulatedErrors = {};
    const emulatedTouched = {};
    
    // Create field names and populate errors/touched for each mapping
    symbolMappings.forEach(mapping => {
      const stringToReplaceField = `mapping_${mapping.id}_stringToReplace`;
      const replacementStringField = `mapping_${mapping.id}_replacementString`;
      
      // Set touched to true (since user has interacted with these fields)
      emulatedTouched[stringToReplaceField] = true;
      emulatedTouched[replacementStringField] = true;
      
      // Set errors if they exist
      if (mappingErrors[mapping.id]?.stringToReplace) {
        emulatedErrors[stringToReplaceField] = mappingErrors[mapping.id].stringToReplace;
      }
      if (mappingErrors[mapping.id]?.replacementString) {
        emulatedErrors[replacementStringField] = mappingErrors[mapping.id].replacementString;
      }
    });
    
    return { errors: emulatedErrors, touched: emulatedTouched };
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;
  const mappingErrors = getMappingValidationErrors();
  const hasMappingErrors = Object.keys(mappingErrors).length > 0;
  const mappingFormikStructure = createMappingFormikStructure();

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors() || hasMappingErrors) {
    saveAndCloseDisabled = true;
  }
  
  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure symbol and timeframe
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure symbol and timeframe</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strSymbol */}
                  {/* ---------------------------------------------------------------------- */}
                  <FastField name="strSymbol">
                    {({ field, meta }) => (
                      <Box>
                        <SmartParameter value="Symbol" helpReference="HandleSymbolAndTimeframe" helpText="" helpLink="" fieldName="strSymbol" touched={touched} errors={errors}/>
                        <SmartTextInput
                          textFieldName="strSymbol"
                          defaultLimits={DEFAULT_LIMITS}
                          formik={formik}
                        />
                      </Box>
                    )}
                  </FastField>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nTimeframe */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter value="Timeframe" helpReference="HandleSymbolAndTimeframe" helpText="" helpLink="" fieldName="nTimeframe"  touched={touched} errors={errors}/>
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nTimeframe")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nTimeframe && errors.nTimeframe)}
                      >
                        {TIMEFRAME_OPTIONS.map((option) => (
                          <ModalContentMenuItem key={option.value} value={option.value}>
                            <Box>
                              <Typography variant="contentSmall">{option.label}</Typography>
                            </Box>
                          </ModalContentMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Symbol Mappings */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Box sx={{ mb: 1 }}>
                      <SmartParameter value={`Symbol name mappings (${symbolMappings.length}/${MAX_SYMBOL_MAPPINGS})`} helpReference="HandleSymbolAndTimeframe" helpText="" helpLink="" fieldName="Symbol name mappings"/>
                    </Box>
                    <Stack spacing={2}>
                      {symbolMappings.length < MAX_SYMBOL_MAPPINGS && (
                        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={addNewMapping}>
                          Add new symbol name mapping
                        </B1Tiny>
                      )}

                      {symbolMappings.map((mapping) => (
                        <Box
                          key={mapping.id}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            p: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                          }}
                        >
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter value="Remove mapping" helpReference="HandleSymbolAndTimeframe" helpText="" helpLink="" fieldName="Remove mapping"/>
                                <IconButton sx={{ mt: "9px" }} size="small" onClick={() => removeMapping(mapping.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Box>
                                <SmartParameter value="Used in live trading" helpReference="HandleSymbolAndTimeframe" helpText="" helpLink="" fieldName="Used in live trading"/>
                                <ModalContentSwitch
                                  sx={{ mt: "12px" }}
                                  checked={mapping.useLive}
                                  onChange={(e) => updateMapping(mapping.id, "useLive", e.target.checked)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter value="Used in strategy testing" helpReference="HandleSymbolAndTimeframe" helpText="" helpLink="" fieldName="Used in strategy testing"/>
                                <ModalContentSwitch
                                  sx={{ mt: "12px" }}
                                  checked={mapping.useStrategy}
                                  onChange={(e) => updateMapping(mapping.id, "useStrategy", e.target.checked)}
                                />
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <SmartParameter 
                                  value="String to replace" 
                                  helpReference="HandleSymbolAndTimeframe"
                                  helpText="" 
                                  helpLink="" 
                                  fieldName={`mapping_${mapping.id}_stringToReplace`}
                                  touched={mappingFormikStructure.touched}
                                  errors={mappingFormikStructure.errors}
                                />
                                <ModalContentTextField
                                  size="small"
                                  fullWidth
                                  value={mapping.stringToReplace}
                                  onChange={(e) => updateMapping(mapping.id, "stringToReplace", e.target.value)}
                                  error={Boolean(mappingErrors[mapping.id]?.stringToReplace)}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <SmartParameter 
                                  value="String it is replaced with" 
                                  helpReference="HandleSymbolAndTimeframe"
                                  helpText="" 
                                  helpLink="" 
                                  fieldName={`mapping_${mapping.id}_replacementString`}
                                  touched={mappingFormikStructure.touched}
                                  errors={mappingFormikStructure.errors}
                                />
                                <ModalContentTextField
                                  size="small"
                                  fullWidth
                                  value={mapping.replacementString}
                                  onChange={(e) => updateMapping(mapping.id, "replacementString", e.target.value)}
                                  error={Boolean(mappingErrors[mapping.id]?.replacementString)}
                                />
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton fullWidth size="large" type="button" onClick={handleReset} loading={isSubmitting} sx={{ mt: 1 }}>
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});

export default SymbolAndTimeframeConfigure;
